import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DatePicker,
} from '@roger/ui-components';
import type { OEType, SeverityType } from '../types/codingData.types';

interface DiagnosisFieldsEditorProps {
  oe?: OEType;
  onsetDate?: Date | null;
  severity?: SeverityType;
  onOEChange?: (value: OEType) => void;
  onOnsetDateChange?: (value: Date | null) => void;
  onSeverityChange?: (value: SeverityType) => void;
  variant?: 'compact' | 'full';
  disabled?: boolean;
}

// Sentinel value for "none" selection (Radix Select doesn't allow empty strings)
const NONE_VALUE = '__none__';

// O/E options based on Kinnser UI
const OE_OPTIONS: { value: string; label: string }[] = [
  { value: NONE_VALUE, label: '—' },
  { value: 'Onset', label: 'Onset' },
  { value: 'Exacerbation', label: 'Exacerbation' },
];

// Severity options based on Kinnser UI (0-4 scale)
const SEVERITY_OPTIONS: { value: string; label: string }[] = [
  { value: NONE_VALUE, label: '—' },
  { value: '0', label: '0' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
];

/**
 * DiagnosisFieldsEditor - Inline editor for O/E, Date, and Severity fields
 *
 * Displays three fields in a row:
 * - O/E: Onset/Exacerbation dropdown
 * - Date: Date picker (onset date)
 * - Severity: 0-4 scale dropdown
 */
const DiagnosisFieldsEditor: React.FC<DiagnosisFieldsEditorProps> = ({
  oe,
  onsetDate,
  severity,
  onOEChange,
  onOnsetDateChange,
  onSeverityChange,
  variant = 'compact',
  disabled = false,
}) => {
  // Handle date change
  const handleDateChange = (date: Date | undefined) => {
    if (onOnsetDateChange) {
      onOnsetDateChange(date ?? null);
    }
  };

  // Handle O/E change
  const handleOEChange = (value: string) => {
    if (onOEChange) {
      onOEChange(value === NONE_VALUE ? null : (value as OEType));
    }
  };

  // Handle severity change
  const handleSeverityChange = (value: string) => {
    if (onSeverityChange) {
      onSeverityChange(value === NONE_VALUE ? null : (value as SeverityType));
    }
  };

  const isCompact = variant === 'compact';

  return (
    <div
      className={`flex items-center ${isCompact ? 'gap-2' : 'gap-4'} flex-wrap`}
    >
      {/* O/E Field */}
      <div className={`flex flex-col ${isCompact ? 'gap-0.5' : 'gap-1'}`}>
        <label
          className={`text-gray-500 font-medium ${isCompact ? 'text-[10px]' : 'text-xs'}`}
        >
          O/E
        </label>
        <Select
          value={oe ?? NONE_VALUE}
          onValueChange={handleOEChange}
          disabled={disabled}
        >
          <SelectTrigger
            className={`${isCompact ? 'h-7 w-[120px] text-xs' : 'h-8 w-[135px] text-sm'}`}
          >
            <SelectValue placeholder='—' />
          </SelectTrigger>
          <SelectContent>
            {OE_OPTIONS.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Date Field */}
      <div className={`flex flex-col ${isCompact ? 'gap-0.5' : 'gap-1'}`}>
        <label
          className={`text-gray-500 font-medium ${isCompact ? 'text-[10px]' : 'text-xs'}`}
        >
          Date
        </label>
        <DatePicker
          date={onsetDate ?? undefined}
          onDateChange={handleDateChange}
          placeholder='—'
          className={`${isCompact ? 'h-7 w-[110px] text-xs' : 'h-8 w-[140px] text-sm'}`}
          yearRange={{ fromYear: 2000, toYear: new Date().getFullYear() + 1 }}
          formatString='MM/dd/yyyy'
          showClearButton={!disabled}
        />
      </div>

      {/* Severity Field */}
      <div className={`flex flex-col ${isCompact ? 'gap-0.5' : 'gap-1'}`}>
        <label
          className={`text-gray-500 font-medium ${isCompact ? 'text-[10px]' : 'text-xs'}`}
        >
          Severity
        </label>
        <Select
          value={severity ?? NONE_VALUE}
          onValueChange={handleSeverityChange}
          disabled={disabled}
        >
          <SelectTrigger
            className={`${isCompact ? 'h-7 w-[60px] text-xs' : 'h-8 w-[80px] text-sm'}`}
          >
            <SelectValue placeholder='—' />
          </SelectTrigger>
          <SelectContent>
            {SEVERITY_OPTIONS.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default DiagnosisFieldsEditor;
